/**
 * Mode dégradé (CP-12) — la feuille de coupure scannée, et ce qui en sort.
 *
 * Le chemin complet : on imprime le gabarit à l'avance, on le remplit à la
 * main pendant la coupure, on le scanne au retour du courant, la lecture
 * pré-remplit une grille, l'agent la vérifie, et seulement alors les entrées
 * rejoignent le registre d'affluences avec leur heure réelle.
 *
 * Voir `coupure/feuille.ts` pour la relecture elle-même.
 */
import { z } from 'zod';
import { CHAMPS_FEUILLE } from '../coupure/feuille.js';
import { DEFAUTS_PRISE_DE_VUE } from '../rules/prise-de-vue.js';
import { schemaBilanLecture } from '../coupure/bilan-lecture.js';
export const STATUTS_FEUILLE_COUPURE = [
    /** Le scan est archivé, la lecture automatique n'a pas encore rendu sa copie. */
    'EN_LECTURE',
    /** La grille est là : elle se vérifie, se corrige, puis se ressaisit. */
    'A_RESSAISIR',
    /** Les entrées sont au registre. La feuille reste consultable : c'est la preuve. */
    'RESSAISIE',
    /** Feuille abandonnée — illisible, en double, ou hors délai. */
    'ABANDONNEE',
];
export const STATUTS_LECTURE_FEUILLE = ['EN_ATTENTE', 'FAITE', 'INDISPONIBLE'];
/** Les deux seules valeurs libres du gabarit : tout le reste vient du référentiel. */
export const schemaLigneFeuille = z.object({
    numeroLigne: z.number().int().positive().max(999),
    /** `HH:MM`. Vide tant que l'agent ne l'a pas renseignée : sans elle, rien ne s'écrit. */
    heure: z
        .string()
        .trim()
        .regex(/^$|^([01]\d|2[0-3]):[0-5]\d$/u, 'Heure attendue sous la forme 08:15.'),
    matricule: z.string().trim().max(40),
    nom: z.string().trim().max(160),
    prestation: z.string().trim().max(80),
    observation: z.string().trim().max(300),
    /** Champs pré-remplis par la lecture et pas encore confirmés par l'agent. */
    aVerifier: z.array(z.enum(CHAMPS_FEUILLE)).max(CHAMPS_FEUILLE.length),
    /**
     * La personne désignée. Nulle : la ligne n'est pas ressaisissable — elle
     * attend qu'on la retrouve, ou qu'on l'écarte en connaissance de cause.
     */
    personneId: z.uuid().nullable(),
    /**
     * Écartée par l'agent : patient finalement introuvable, ligne barrée sur le
     * papier, doublon. Elle reste écrite dans la feuille — une ligne qu'on
     * laisse tomber sans trace est exactement ce qu'on veut éviter.
     */
    ecartee: z.boolean(),
    /** Pourquoi elle est écartée, en clair. Exigé à l'écran. */
    motifEcart: z.string().trim().max(200).nullable(),
});
/** Ce que le serveur ajoute à une ligne : qui est cette personne, et qui elle pourrait être. */
export const schemaLigneFeuilleResolue = schemaLigneFeuille.extend({
    personne: z
        .object({
        id: z.uuid(),
        nom: z.string(),
        matricule: z.string().nullable(),
        societe: z.string().nullable(),
    })
        .nullable(),
    /**
     * Les personnes que le matricule ou le nom lu désigne, quand la lecture
     * n'a pas tranché. Jamais choisies d'office : c'est l'agent qui désigne.
     */
    candidats: z
        .array(z.object({
        id: z.uuid(),
        nom: z.string(),
        matricule: z.string().nullable(),
        societe: z.string().nullable(),
        /** 0 à 1 quand la personne est proposée sur un nom approché ; nulle sinon. */
        ressemblance: z.number().min(0).max(1).nullable(),
    }))
        .max(5),
    /**
     * Comment la personne a été retrouvée. `NOM_APPROCHE` : aucun dossier ne
     * porte exactement le nom lu, ceux-là en sont les plus proches — une
     * faute de lecture ressemble autant qu'un homonyme (JEAN / JEANNE : 0,73),
     * donc l'agent confirme toujours.
     */
    rapprochement: z.enum(['MATRICULE', 'NOM', 'NOM_APPROCHE']).nullable(),
    /**
     * Le nom lu sur le papier ne ressemble pas à celui de la personne que le
     * matricule désigne. Signalé, jamais bloquant — l'écriture d'un agent
     * pressé ne prouve rien, mais elle mérite un regard.
     */
    nomDivergent: z.boolean(),
});
export const schemaPageFeuille = z.object({
    id: z.uuid(),
    ordre: z.number().int().positive(),
    nomFichier: z.string().nullable(),
    type: z.string(),
    octets: z.number().int().nonnegative(),
});
export const schemaFeuilleCoupure = z.object({
    id: z.uuid(),
    site: z.object({ id: z.uuid(), nom: z.string() }),
    /** Le jour de la coupure : `YYYY-MM-DD`. Les heures du papier s'y rattachent. */
    dateCoupure: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
    statut: z.enum(STATUTS_FEUILLE_COUPURE),
    pages: z.array(schemaPageFeuille),
    lecture: z.object({
        statut: z.enum(STATUTS_LECTURE_FEUILLE),
        message: z.string().nullable(),
        /** Le gabarit a été reconnu à son marqueur imprimé. */
        gabaritReconnu: z.boolean(),
        /** Un tableau a été lu sans que ses colonnes soient reconnues : voir l'import DNS. */
        aUnTableau: z.boolean(),
        /** Photo réduite ou floue, page par page : la lecture a eu lieu, mais elle est desservie. */
        priseDeVue: z.array(z.object({ page: z.number().int().positive(), defaut: z.enum(DEFAUTS_PRISE_DE_VUE) })),
    }),
    lignes: z.array(schemaLigneFeuilleResolue),
    creeeLe: z.iso.datetime(),
    ressaisieLe: z.iso.datetime().nullable(),
    /** Bilan de la ressaisie, une fois faite. */
    rapport: z
        .object({
        creees: z.number().int().nonnegative(),
        ecartees: z.number().int().nonnegative(),
        echecs: z.array(z.object({ numeroLigne: z.number().int(), motif: z.string() })),
        /** Ce que la lecture a fait gagner ; absent d'une feuille tapée à la main, ou d'avant le 07/10. */
        lecture: schemaBilanLecture.optional(),
    })
        .nullable(),
});
/** Le résumé d'une feuille, pour la liste. */
export const schemaFeuilleCoupureResumee = schemaFeuilleCoupure
    .pick({ id: true, dateCoupure: true, statut: true, creeeLe: true, ressaisieLe: true })
    .extend({
    siteNom: z.string(),
    nbLignes: z.number().int().nonnegative(),
    nbRessaisies: z.number().int().nonnegative(),
});
export const schemaDepotFeuille = z
    .object({
    dateCoupure: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'Date de la coupure attendue.'),
})
    .strict();
/** Bornes du gabarit : une feuille de coupure, pas un registre annuel. */
export const PAGES_MAX_FEUILLE = 10;
export const LIGNES_MAX_FEUILLE = 300;
export const schemaGrilleFeuille = z
    .object({ lignes: z.array(schemaLigneFeuille).max(LIGNES_MAX_FEUILLE) })
    .strict();
//# sourceMappingURL=coupure.js.map